import { useState, useRef, useEffect } from 'react';
import { useNamedEntityGroups, useNamedEntityItems, useSimilarResources } from '../../hooks/useResource';
import { ResourceRow } from '../ResourceRow';
import { PaginationControls } from '../PaginationControls';
import { useResourceViewerStore } from '../../stores/resourceViewerStore';
import type { Resource, NamedEntityGroup, EntityHighlight } from '../../types/resource';

interface Props {
  projectId: string;
  collectionId: string;
  resourceId: string;
}

interface HighlightProps {
  /** Highlighting is offered only while the annotated Plain Text view is showing. */
  canHighlightEntities?: boolean;
  highlightCategories?: string[];
  highlightEntity?: EntityHighlight | null;
  onToggleHighlightCategory?: (category: string) => void;
  onSelectHighlightEntity?: (entity: EntityHighlight) => void;
}

type View = 'namedEntities' | 'similarResources';

function titleCase(s: string) {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

const ENTITY_ICONS: Record<string, string> = {
  organization: 'far fa-building fa-fw',
  phone: 'fas fa-mobile-alt fa-fw',
  person: 'fas fa-male fa-fw',
  money: 'far fa-money-bill-alt fa-fw',
  location: 'fas fa-map-pin fa-fw',
  percent: 'fas fa-percent fa-fw',
  date: 'far fa-calendar-alt fa-fw',
  time: 'far fa-clock fa-fw',
  event: 'fas fa-calendar-check fa-fw',
};

// Tint of the highlighter icon when a category is on — same swatches the
// `.highlight-*` text rules in _iio.scss paint the entities with.
const ENTITY_COLORS: Record<string, string> = {
  person: '#a59fef',
  organization: '#aee7ea',
  date: '#ccdfd9',
  time: '#ccdfd9',
  location: '#edcc8b',
  percent: '#e4e7d4',
  money: '#e4e7d4',
  event: '#fceb8e',
  commercial_item: '#f5cc82',
};

function ExpandedGroupItems({
  projectId,
  collectionId,
  resourceId,
  typeValue,
  selectable,
  highlightEntity,
  onSelectHighlightEntity,
}: {
  projectId: string;
  collectionId: string;
  resourceId: string;
  typeValue: string;
  selectable?: boolean;
  highlightEntity?: EntityHighlight | null;
  onSelectHighlightEntity?: (entity: EntityHighlight) => void;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useNamedEntityItems(projectId, collectionId, resourceId, typeValue, true);

  const items = data?.pages.flatMap((p) => p.expandedGroupItems ?? []) ?? [];

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) return <div style={{ padding: '6px 0 6px 16px', color: '#888' }}>Loading…</div>;
  if (items.length === 0) return <div style={{ padding: '6px 0 6px 16px', color: '#888' }}>No items</div>;

  return (
    <div>
      {items.map((item) => {
        const active =
          !!highlightEntity &&
          highlightEntity.type === item.type.value &&
          highlightEntity.value === item.value;
        return (
          <div
            key={item.id}
            data-testid="viewer-entity-item"
            onClick={
              selectable
                ? () => onSelectHighlightEntity?.({ type: item.type.value, value: item.value })
                : undefined
            }
            title={selectable ? 'Highlight in Plain Text view' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              height: 35,
              padding: '0 8px 0 24px',
              borderTop: '1px solid #f0f0f0',
              fontSize: 14,
              cursor: selectable ? 'pointer' : undefined,
              background: active ? '#eef3fb' : undefined,
              fontWeight: active ? 600 : undefined,
            }}
          >
            {titleCase(item.value)}
          </div>
        );
      })}
      <div ref={sentinelRef} style={{ height: 1 }} />
      {isFetchingNextPage && <div style={{ padding: '6px 0 6px 16px', color: '#888', fontSize: 12 }}>Loading more…</div>}
    </div>
  );
}

function NamedEntitiesView({
  projectId,
  collectionId,
  resourceId,
  canHighlightEntities,
  highlightCategories = [],
  highlightEntity,
  onToggleHighlightCategory,
  onSelectHighlightEntity,
}: Props & HighlightProps) {
  const { data, isLoading } = useNamedEntityGroups(projectId, collectionId, resourceId);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const groups = data?.groups ?? [];

  function toggleGroup(typeValue: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(typeValue)) next.delete(typeValue);
      else next.add(typeValue);
      return next;
    });
  }

  if (isLoading) return <div className="alert alert-info" style={{ margin: '10px 0' }}>Loading named entities…</div>;

  if (groups.length === 0) {
    return (
      <div className="alert alert-info alert-block">
        <h4>No named entities found</h4>
        <p>This document did not have any named entities identified</p>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      {groups.map((group: NamedEntityGroup) => {
        const isOpen = expanded.has(group.type.value);
        const icon = ENTITY_ICONS[group.type.value];
        const categoryOn = highlightCategories.includes(group.type.value);
        return (
          <div key={group.type.value} style={{ borderBottom: '1px solid #eee' }} data-testid="viewer-entity-group-item">
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                background: '#f5f5f5',
                borderBottom: isOpen ? '1px solid #ddd' : 'none',
              }}
            >
              <button
                type="button"
                onClick={() => toggleGroup(group.type.value)}
                data-testid="viewer-entity-group-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                  height: 35,
                  padding: '0 10px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                <i
                  className={`fas ${isOpen ? 'fa-minus-square' : 'fa-plus-square'}`}
                  style={{ marginRight: 8, fontSize: 13, color: '#555' }}
                />
                {icon && <i className={icon} style={{ marginRight: 6, color: '#555' }} />}
                {group.type.displayName}
              </button>
              {/* Category highlighter — Plain Text view only. */}
              {canHighlightEntities && (
                <button
                  type="button"
                  className="btn btn-link"
                  title={`Highlight all ${group.type.displayName.toLowerCase()} in Plain Text view`}
                  aria-pressed={categoryOn}
                  onClick={() => onToggleHighlightCategory?.(group.type.value)}
                  data-testid="viewer-entity-highlight-btn"
                  style={{ padding: '0 10px', border: 'none', background: 'transparent', cursor: 'pointer' }}
                >
                  <i
                    className="fas fa-highlighter"
                    style={{
                      fontSize: 18,
                      color: categoryOn ? (ENTITY_COLORS[group.type.value] ?? '#666') : '#ccc',
                    }}
                  />
                </button>
              )}
            </div>
            {isOpen && (
              <ExpandedGroupItems
                projectId={projectId}
                collectionId={collectionId}
                resourceId={resourceId}
                typeValue={group.type.value}
                selectable={canHighlightEntities}
                highlightEntity={highlightEntity}
                onSelectHighlightEntity={onSelectHighlightEntity}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function SimilarResourcesView({ projectId, collectionId, resourceId }: Props) {
  const [page, setPage] = useState(0);
  const { data, isLoading } = useSimilarResources(projectId, collectionId, resourceId, page);
  const open = useResourceViewerStore((s) => s.open);

  const resources = data?.resources ?? [];
  const totalPages = data?.totalPages ?? 1;

  function openResource(r: Resource) {
    open({ projectId: r.projectId, collectionId: r.collectionId, resourceId: r.id });
  }

  if (isLoading) return <div className="alert alert-info" style={{ margin: '10px 0' }}>Loading similar resources…</div>;

  if (resources.length === 0) {
    return (
      <div className="alert alert-info alert-block">
        <h4>No similar resources found</h4>
        <p>This document did not have other documents that were similar</p>
      </div>
    );
  }

  // Mirror RelatedTab: the container scrolls and `.iio-resource-medias` is a normal
  // block child (auto height), so ResourceRow's `height: 100%` (needed inside ag-grid
  // cells) collapses to content here instead of stretching each row to fill the pane.
  // hideThumbnail + noIndent match Angular's similar grid (row-selection="CheckboxOff",
  // no-indent="true").
  return (
    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      <PaginationControls currentPage={page} totalPages={totalPages} onGoTo={setPage} testId="viewer-similar-top-pagination-list" />
      <div className="iio-resource-medias" data-testid="viewer-similar-list">
        {resources.map((r) => (
          <ResourceRow key={r.id} resource={r} onClick={openResource} hideThumbnail noIndent />
        ))}
      </div>
      <PaginationControls currentPage={page} totalPages={totalPages} onGoTo={setPage} testId="viewer-similar-bottom-pagination-list" />
    </div>
  );
}

export default function AnalyticsTab({
  projectId,
  collectionId,
  resourceId,
  ...highlight
}: Props & HighlightProps) {
  const [view, setView] = useState<View>('namedEntities');

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 10, boxSizing: 'border-box', overflow: 'hidden' }} data-testid="viewer-analytics-panel">
      <div style={{ flexShrink: 0, marginBottom: 8 }}>
        {/* Toggle on its own row (right-aligned), heading below it — matches Angular. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div className="btn-group">
            <button
              type="button"
              className={`btn btn-default${view === 'namedEntities' ? ' active' : ''}`}
              onClick={() => setView('namedEntities')}
              data-testid="viewer-analytics-entities-btn"
            >
              Named Entities
            </button>
            <button
              type="button"
              className={`btn btn-default${view === 'similarResources' ? ' active' : ''}`}
              onClick={() => setView('similarResources')}
              data-testid="viewer-analytics-similar-btn"
            >
              Similar Resources
            </button>
          </div>
        </div>
        <h4 style={{ margin: '8px 0 0' }}>
          {view === 'namedEntities' ? 'Named Entities' : 'Similar'}
        </h4>
      </div>

      {view === 'namedEntities' ? (
        <NamedEntitiesView
          projectId={projectId}
          collectionId={collectionId}
          resourceId={resourceId}
          {...highlight}
        />
      ) : (
        <SimilarResourcesView projectId={projectId} collectionId={collectionId} resourceId={resourceId} />
      )}
    </div>
  );
}
